import React, { useRef } from "react";
import FocusTrap from "focus-trap-react";
import { useCompareV2Selector } from "../../redux/store";
import useCompareToggle from "../../../../shared-logic/features/compare/hooks/useCompareToggle";
import useInitNewCompareSingleDirectOpen from "../../hooks/useInitNewCompareSingleDirectOpen";
import NewCarHeader from "./NewCarHeader";
import CompareDetails from "./highlight/CompareDetails";
import CtaContainer from "./CtaContainer";
import CarSpecs from "./specifications/CarSpecs";
import EngineSpecs from "./specifications/EngineSpecs";
import * as Styles from "./styles/CompareStyles";
import useOverlayScrollSync from "../../hooks/useOverlayScrollSync";
import { MAIN_NAV_CLASSNAME_TOYOTA } from "../../../../common-deprecated/constants";
import UnsetZIndexHelper from "../../../../common-deprecated/components/UnsetZIndexHelper";
import ScrollIndicatorDown from "../../../../common-deprecated/components/scroll-indicator/ScrollIndicatorDown";
import { useBreakpoint } from "../../../../common-deprecated/themes/common";
import SrOnly from "../../../../common-deprecated/styles/v2/toyota/utilities/ScreenreaderOnly";
import { CompareV2Label } from "../../utils/labels";
import useScrollbarWidth from "../../hooks/useScrollbarWidth";
import { NcCompareContext } from "../../../../common-deprecated/types/NewCarCompareTypes";
import useDisclaimerContent from "../../hooks/useDisclaimerContent";
import { IntegratedLegalBannerDisclaimer } from "../../../shared-components/toyota/IntegratedLegalBannerDisclaimer";

const CompareV2View = (): JSX.Element | null => {
    const cars = useCompareV2Selector((state) => state.newCarCompare.grades);
    const compareV2Standalone = useCompareV2Selector((state) => state.compareV2Settings.compareV2Standalone);
    const isLeasing = useCompareV2Selector(
        (state) => state.newCarCompare.ncCompareContext === NcCompareContext.Leasing,
    );

    const topOfCompareRef = useRef<HTMLElement>(null);
    const bodyRef = useRef<HTMLDivElement>(null);
    const { show, expanded, toggleExpanded, scrollToTop, animationEnded, toggleAnimationEnded } = useCompareToggle({
        topOfCompareRef,
        bodyRef,
        hasCars: !!cars.length,
        standalone: compareV2Standalone,
    });
    const isMobile = useBreakpoint("down", "md");

    useInitNewCompareSingleDirectOpen(toggleExpanded, expanded, animationEnded);
    useOverlayScrollSync(bodyRef);

    const scrollbarWidth = useScrollbarWidth(bodyRef);

    const nlFinanceDisclaimerData = useDisclaimerContent();

    return show ? (
        <FocusTrap active={expanded}>
            <Styles.Wrapper expanded={expanded} onTransitionEnd={() => toggleAnimationEnded(true)} role="table">
                {/*Some navigations are also hidden with the toggleExpanded (hideSiteElements) method inside useCompareToggle*/}
                <UnsetZIndexHelper enabled={expanded} selectors={[MAIN_NAV_CLASSNAME_TOYOTA]} />
                <SrOnly as="h2">
                    <CompareV2Label label="compareNCA11yCompare" />
                </SrOnly>
                <NewCarHeader
                    expanded={expanded}
                    toggleExpanded={toggleExpanded}
                    onShowSelectNewGrade={scrollToTop}
                    scrollbarWidth={scrollbarWidth}
                />
                {/* We add the or-modal-body classname so salesman scrolls can sync to billboard */}
                <Styles.Body ref={bodyRef} className="or-modal-body">
                    <Styles.Container isWide={compareV2Standalone || isLeasing} role="rowgroup">
                        <span ref={topOfCompareRef} />
                        <CompareDetails />
                        {!!cars.length && (
                            <div role="rowgroup">
                                <CtaContainer />
                                <CarSpecs />
                                <EngineSpecs />
                                <CtaContainer />
                                <IntegratedLegalBannerDisclaimer data={nlFinanceDisclaimerData} />
                            </div>
                        )}
                    </Styles.Container>
                </Styles.Body>
                {isMobile && <ScrollIndicatorDown viewportRef={bodyRef} recheckKey={String(expanded)} />}
            </Styles.Wrapper>
        </FocusTrap>
    ) : null;
};

export default CompareV2View;
